/**
 * DischargeSkillEffect — Lightning tree Discharge path active.
 *
 * Dumps all stored Static charge in a radial AoE burst centered on the player.
 * Damage and blast radius scale linearly with current charge level — empty
 * yields nothing worth mentioning, full charge clears the room. All Static
 * charge is consumed on activation regardless of hit outcome.
 *
 * Charge scaling (linear interpolation):
 *   ratio = consumedCharge / maxCharge
 *   radius = (baseRadiusTiles + ratio * additionalRadiusTiles) * radiusMultiplier
 *   damage = baseDamageMultiplier + ratio * additionalDamageMultiplier
 *
 * Fires an onDetonation callback after the burst resolves, allowing Afterburn
 * and other downstream listeners to react to the discharge event.
 *
 * Upgrade keys (via resolver, scope: 'discharge'):
 * - radiusMultiplier: multiplicative — blast radius scaling (Surge: 1.3x)
 * - stunDurationMs: additive — stun applied to hit targets (Surge: 300ms)
 * - trailDurationMs: additive — charge_trail zone duration (Trail: 2000ms)
 * - trailDamage: additive — damage dealt to enemies entering the trail (Trail: 4)
 * - trailStaggerMs: additive — stagger applied on trail contact (Trail: 250ms)
 *
 * Trail hooks into dashing, not Discharge activation: the integration layer
 * calls onDash() whenever the player dashes, and Discharge spawns a charge_trail
 * zone if Trail is unlocked (trailDurationMs > 0).
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see lightning-spec.md section Discharge, Trail
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DischargeAoEConfig {
  /** Burst radius in tiles centered on the player. */
  readonly radiusTiles: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

export interface DischargeAoEResult {
  /** Number of enemies caught in the blast (0 = whiff). */
  readonly enemiesHit: number;
}

export interface DischargeTrailZoneConfig {
  readonly type: 'charge_trail';
  readonly durationMs: number;
  readonly entryDamage: number;
  readonly entryStaggerMs: number;
  readonly sourceSkillId: string;
}

export interface DischargeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly aoe: {
    burst(config: DischargeAoEConfig): DischargeAoEResult;
  };
  readonly charge: {
    consume(chargeId: string): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    isImmune(defId: string): boolean;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  readonly onDetonation?: () => void;
  /** Zone system — required when Trail upgrade is unlocked. */
  readonly zones?: {
    create(config: DischargeTrailZoneConfig): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DischargeConfig {
  readonly cooldownMs: number;
  /** Maximum charge capacity — used to compute the charge ratio for scaling. */
  readonly maxCharge: number;
  /** Base blast radius in tiles at zero charge. */
  readonly baseRadiusTiles: number;
  /** Additional radius added at full charge (linear scaling). */
  readonly additionalRadiusTiles: number;
  /** Base damage multiplier at zero charge. */
  readonly baseDamageMultiplier: number;
  /** Additional damage multiplier added at full charge (linear scaling). */
  readonly additionalDamageMultiplier: number;
}

const DEFAULT_CONFIG: DischargeConfig = {
  cooldownMs: 800,
  maxCharge: 100,
  baseRadiusTiles: 1.0,
  additionalRadiusTiles: 2.0,
  baseDamageMultiplier: 0.2,
  additionalDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DischargeSkillEffect implements SkillEffect {
  readonly skillId = 'discharge';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: DischargeDeps;
  private readonly config: DischargeConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _hitsLanded = 0;
  private _stunApplications = 0;
  private _totalChargeConsumed = 0;
  private _lastChargeConsumed = 0;
  private _lastChargeRatio = 0;
  private _lastBurstRadius = 0;
  private _lastDamageMultiplier = 0;
  private _detonations = 0;
  private _trailsSpawned = 0;

  constructor(deps: DischargeDeps, config: Partial<DischargeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // -- Read-only accessors for simulation inspection --------------------------

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get hitsLanded(): number { return this._hitsLanded; }
  get stunApplications(): number { return this._stunApplications; }
  get totalChargeConsumed(): number { return this._totalChargeConsumed; }
  get lastChargeConsumed(): number { return this._lastChargeConsumed; }
  get lastChargeRatio(): number { return this._lastChargeRatio; }
  get lastBurstRadius(): number { return this._lastBurstRadius; }
  get lastDamageMultiplier(): number { return this._lastDamageMultiplier; }
  get detonations(): number { return this._detonations; }
  get trailsSpawned(): number { return this._trailsSpawned; }

  // -- Trail (dash-triggered zone) --------------------------------------------

  /**
   * Called by the integration layer whenever the player dashes. If Trail is
   * unlocked (trailDurationMs > 0 via resolver), spawns a charge_trail zone
   * along the dash path. This is a permanent passive modification to dashing —
   * independent of Discharge activation.
   */
  onDash(): void {
    const trailDurationMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailDurationMs');
    if (trailDurationMs <= 0) return;
    if (!this.deps.zones) return;

    const trailDamage = this.deps.resolver.getAdditiveEffect('discharge', 'trailDamage');
    const trailStaggerMs = this.deps.resolver.getAdditiveEffect('discharge', 'trailStaggerMs');

    this.deps.zones.create({
      type: 'charge_trail',
      durationMs: trailDurationMs,
      entryDamage: trailDamage,
      entryStaggerMs: trailStaggerMs,
      sourceSkillId: 'discharge',
    });

    this._trailsSpawned++;
  }

  // -- SkillEffect interface --------------------------------------------------

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle — skill attempt failed, no charge consumed, no cooldown
    if (!this.deps.rollSkillSuccess('discharge')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // 1. Consume all static charge
    const consumed = this.deps.charge.consume('static_charge');
    this._lastChargeConsumed = consumed;
    this._totalChargeConsumed += consumed;

    // 2. Compute charge ratio (0 at empty, 1 at full)
    const maxCharge = this.config.maxCharge;
    const ratio = maxCharge > 0 ? Math.min(consumed / maxCharge, 1.0) : 0;
    this._lastChargeRatio = ratio;

    // 3. Compute charge-scaled radius with Surge multiplier
    const baseRadius = this.config.baseRadiusTiles + ratio * this.config.additionalRadiusTiles;
    const radiusMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'discharge', 'radiusMultiplier',
    );
    this._lastBurstRadius = baseRadius * radiusMultiplier;

    // 4. Compute charge-scaled damage multiplier
    this._lastDamageMultiplier =
      this.config.baseDamageMultiplier + ratio * this.config.additionalDamageMultiplier;

    // 5. Fire AoE burst centered on player
    const result = this.deps.aoe.burst({
      radiusTiles: this._lastBurstRadius,
      damageMultiplier: this._lastDamageMultiplier,
      sourceSkillId: 'discharge',
    });

    this._activations++;

    // 6. Apply stun to hit targets if Surge is unlocked (stunDurationMs > 0)
    if (result.enemiesHit > 0) {
      this._hitsLanded++;

      const stunMs = this.deps.resolver.getAdditiveEffect('discharge', 'stunDurationMs');
      if (stunMs > 0 && !this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'discharge',
          durationMs: stunMs,
        });
        this._stunApplications++;
      }
    }

    // 7. Fire detonation event for Afterburn / downstream listeners
    this._detonations++;
    this.deps.onDetonation?.();

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void { /* instant skill — nothing to deactivate */ }
  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
